import { useCallback, useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { logger } from '@utils/logger';

interface UseTablePaginationOptions {
  page: number;
  size?: number;
  path?: string;
  params?: Record<string, unknown>;
  sortingTable?: Record<string, unknown>;
  tableName?: string;
  columnsSorting?: Record<string, unknown>;
  argument?: Record<string, unknown>;
  requestTablePageChange?: any;
  onPageChange?: (page: number) => void;
}

export const useTablePagination = ({
  page,
  size,
  path,
  params,
  sortingTable,
  tableName,
  columnsSorting,
  argument,
  requestTablePageChange,
  onPageChange,
}: UseTablePaginationOptions) => {
  const dispatch = useAppDispatch();
  const [updatedPage, setPage] = useState<number>(page);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const buildDispatchPayload = (overrides: Record<string, unknown>) => ({
    requestId: Date.now(),
    params: {
      page: updatedPage,
      size,
      ...params,
      ...sortingTable,
      ...overrides,
    },
    pathName: path,
    type: path,
    id: 2,
    argument: argument ?? {},
    ...(argument ?? {}),
    body: tableName ? columnsSorting?.[tableName] : {},
  });

  const handlePageChange = async (operator: boolean) => {
    setIsUpdating(true);
    const newPage = operator ? updatedPage + 1 : updatedPage - 1;
    try {
      if (requestTablePageChange) {
        await dispatch(
          requestTablePageChange.initiate(
            buildDispatchPayload({ page: newPage }),
            { forceRefetch: true }
          )
        );
      } else {
        onPageChange?.(newPage);
      }
      setPage(newPage);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSizeChange = async (changedSize: number) => {
    setIsUpdating(true);
    setPage(0);
    try {
      if (requestTablePageChange) {
        await dispatch(
          requestTablePageChange.initiate(
            buildDispatchPayload({ page: 0, size: changedSize }),
            { forceRefetch: true }
          )
        );
      }
    } catch (err) {
      logger.log(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!requestTablePageChange) return;
    setIsRefreshing(true);
    try {
      await dispatch(
        requestTablePageChange.initiate(buildDispatchPayload({}), {
          forceRefetch: true,
        })
      );
    } catch (err) {
      logger.log(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    requestTablePageChange,
    dispatch,
    updatedPage,
    size,
    params,
    sortingTable,
    path,
    argument,
    tableName,
    columnsSorting,
  ]);

  return {
    updatedPage,
    setPage,
    isUpdating,
    isRefreshing,
    handlePageChange,
    handleSizeChange,
    handleRefresh,
  };
};
